/**
 * ScoreCard — displays a site health score with band and confidence label.
 *
 * When pagesScanned is 0 (or scoreValue is null), shows a clear "no data"
 * state instead of a misleading number.
 */

import type { ScoreBand, ScoreConfidence } from "../api/product-types.js";

interface ScoreCardProps {
  scoreValue: number | null;
  scoreBand: ScoreBand | null;
  confidence: ScoreConfidence | null;
  /** When 0 pages were scanned, the score is meaningless — show failure state. */
  pagesScanned?: number;
  label?: string;
}

const BAND_LABELS: Record<ScoreBand, string> = {
  strong: "Strong",
  needs_improvement: "Needs improvement",
  accessibility_risk: "Accessibility risk",
  high_risk: "High risk",
};

const BAND_CLASS: Record<ScoreBand, string> = {
  strong: "score-card--strong",
  needs_improvement: "score-card--needs-improvement",
  accessibility_risk: "score-card--at-risk",
  high_risk: "score-card--high-risk",
};

const CONFIDENCE_LABELS: Record<ScoreConfidence, string> = {
  high: "High confidence",
  medium: "Medium confidence",
  low: "Low confidence",
};

export function ScoreCard({
  scoreValue,
  scoreBand,
  confidence,
  pagesScanned,
  label = "Accessibility score",
}: ScoreCardProps) {
  // If we know 0 pages were scanned, the score is meaningless — override.
  const noData =
    scoreValue == null || (pagesScanned != null && pagesScanned === 0);

  if (noData) {
    return (
      <div className="score-card score-card--no-data" aria-label={label}>
        <div className="score-card__value" aria-live="polite">
          —
        </div>
        <div className="score-card__band">
          {pagesScanned === 0
            ? "No pages could be scanned"
            : "No score yet"}
        </div>
        <div className="score-card__label">{label}</div>
      </div>
    );
  }

  const bandClass = scoreBand ? BAND_CLASS[scoreBand] : "";

  return (
    <div
      className={`score-card ${bandClass}`}
      aria-label={`${label}: ${Math.round(scoreValue)} out of 100`}
    >
      <div className="score-card__value" aria-live="polite">
        {Math.round(scoreValue)}
        <span className="score-card__max">/100</span>
      </div>
      <div className="score-card__band">
        {scoreBand ? BAND_LABELS[scoreBand] : "No score yet"}
      </div>
      {confidence && (
        <div className="score-card__confidence">
          {CONFIDENCE_LABELS[confidence]}
        </div>
      )}
      <div className="score-card__label">{label}</div>
    </div>
  );
}
